/**
 * AppTopbar — topbar for the (app) layout.
 * Contains: Cmd+K search trigger, notifications bell, profile menu, dark-mode toggle.
 *
 * M4.4: NotificationBell replaces the placeholder bell button.
 * The bell polls useUnreadCountQuery every 15 s and opens a Popover dropdown on click.
 *
 * Profile menu (DropdownMenu):
 *   - User name + email (read-only header)
 *   - Change password → /settings/password
 *   - Sign out
 */

import { HelpCircle, KeyRound, LogOut, Moon, Search, Sun } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores/auth-store';
import { useLogoutMutation } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

import { NotificationBell } from '@/components/domain/NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function AppTopbar() {
  const { theme, setTheme, toggleCommandPalette } = useUIStore();
  const user = useAuthStore((s) => s.user);
  const logoutMutation = useLogoutMutation();

  // Derive initials for the profile avatar
  const initials = user?.full_name
    ? user.full_name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => (w[0] ?? '').toUpperCase())
        .join('')
    : 'U';

  function cycleTheme() {
    const next: Record<string, 'light' | 'dark' | 'system'> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    };
    setTheme(next[theme] ?? 'system');
  }

  return (
    <header className="h-16 flex items-center justify-between px-6 border-b border-border bg-card shrink-0">
      {/* Left — Cmd+K trigger */}
      <button
        type="button"
        onClick={toggleCommandPalette}
        aria-label="Open command palette (Cmd+K)"
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-md border border-border',
          'text-sm text-muted-foreground bg-muted/50 hover:bg-muted transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
        )}
      >
        <Search size={14} aria-hidden="true" />
        <span className="hidden sm:inline">Search&hellip;</span>
        <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border border-border bg-background px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          ⌘K
        </kbd>
      </button>

      {/* Right — actions */}
      <div className="flex items-center gap-2">
        {/* Dark-mode toggle */}
        <button
          type="button"
          onClick={cycleTheme}
          aria-label={`Current theme: ${theme}. Click to cycle.`}
          className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {theme === 'dark' ? <Moon size={18} aria-hidden="true" /> : <Sun size={18} aria-hidden="true" />}
        </button>

        {/* Help & Guides */}
        <Link
          to="/help"
          aria-label="Help & Guides"
          title="Help & Guides"
          className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <HelpCircle size={18} aria-hidden="true" />
        </Link>

        {/* Notifications bell — M4.4 */}
        <NotificationBell />

        {/* Profile avatar — opens user menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-semibold select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 hover:opacity-90 transition-opacity"
              aria-label={`User menu — ${user?.full_name ?? 'Profile'}`}
            >
              {initials}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            {/* Identity header */}
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-semibold text-foreground truncate">
                  {user?.full_name ?? 'User'}
                </span>
                <span className="text-xs text-muted-foreground truncate">
                  {user?.email ?? ''}
                </span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Change password */}
            <DropdownMenuItem asChild>
              <Link to="/settings/password" className="flex items-center gap-2 cursor-pointer">
                <KeyRound size={14} aria-hidden="true" />
                Change password
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            {/* Sign out */}
            <DropdownMenuItem
              className="flex items-center gap-2 text-destructive focus:text-destructive cursor-pointer"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              <LogOut size={14} aria-hidden="true" />
              {logoutMutation.isPending ? 'Signing out…' : 'Sign out'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
